import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Download,
  X,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  FileJson,
  FileText,
} from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Checkbox from '../ui/Checkbox';
import Badge from '../ui/Badge';
import { useToast } from '../common/ToastContainer';
import basketIoService, {
  BasketBundle,
  ImportPreview,
  ImportPreviewStrategy,
} from '../../services/basketIoService';

interface ImportBasketDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful commit so the parent can refresh + select the new basket. */
  onImported?: (basketId: string, basketName: string, strategyCount: number) => void;
}

type Step = 'upload' | 'preview' | 'confirm';

/**
 * 3-step wizard: Upload (drag-drop) → Preview (edit names + tick selections)
 * → Confirm (final summary). The Preview step is the heavy lifting; Confirm
 * is a deliberate "are you sure" gate before the atomic write.
 *
 * Strategies whose underlying isn't supported in the recipient's tenant are
 * auto-unchecked and locked in Preview, then silently skipped server-side
 * even if a tampered request flips the checkbox.
 */
const ImportBasketDialog: React.FC<ImportBasketDialogProps> = ({
  isOpen,
  onClose,
  onImported,
}) => {
  const { showSuccess, showError } = useToast();

  const [step, setStep] = useState<Step>('upload');
  const [bundle, setBundle] = useState<BasketBundle | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);

  // Editable Preview state
  const [basketName, setBasketName] = useState<string>('');
  const [selections, setSelections] = useState<Record<number, boolean>>({});
  const [nameOverrides, setNameOverrides] = useState<Record<number, string>>({});

  // Async / UI state
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset all state whenever the dialog re-opens.
  useEffect(() => {
    if (isOpen) {
      setStep('upload');
      setBundle(null);
      setPreview(null);
      setBasketName('');
      setSelections({});
      setNameOverrides({});
      setParsing(false);
      setCommitting(false);
      setDragging(false);
    }
  }, [isOpen]);

  // ── Step 1: Upload ──────────────────────────────────────────────────
  const handleFile = async (file: File | undefined | null) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.json')) {
      showError('Please choose a .json file');
      return;
    }
    try {
      setParsing(true);
      const parsed = await basketIoService.readBundleFile(file);
      const previewData = await basketIoService.previewImport(parsed);

      // Default selections: every supported strategy checked, every
      // unsupported one unchecked + locked.
      const initialSelections: Record<number, boolean> = {};
      previewData.strategies.forEach((s) => {
        initialSelections[s.strategy_index] = s.supported;
      });

      setBundle(parsed);
      setPreview(previewData);
      setBasketName(previewData.basket.suggested_name);
      setSelections(initialSelections);
      setNameOverrides({});
      setStep('preview');
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to read bundle';
      showError(msg);
    } finally {
      setParsing(false);
    }
  };

  // ── Step 2 → 3: Preview helpers ─────────────────────────────────────
  const supportedStrategies = useMemo(
    () => preview?.strategies.filter((s) => s.supported) ?? [],
    [preview],
  );
  const unsupportedStrategies = useMemo(
    () => preview?.strategies.filter((s) => !s.supported) ?? [],
    [preview],
  );

  const checkedCount = useMemo(
    () => supportedStrategies.filter((s) => selections[s.strategy_index]).length,
    [supportedStrategies, selections],
  );
  const allSupportedChecked =
    supportedStrategies.length > 0 && checkedCount === supportedStrategies.length;
  const someSupportedChecked = checkedCount > 0 && !allSupportedChecked;

  const toggleAllSupported = () => {
    setSelections((prev) => {
      const next = { ...prev };
      const flipTo = !allSupportedChecked;
      supportedStrategies.forEach((s) => {
        next[s.strategy_index] = flipTo;
      });
      return next;
    });
  };
  const toggleStrategy = (index: number) => {
    setSelections((prev) => ({ ...prev, [index]: !prev[index] }));
  };
  const setStrategyName = (index: number, name: string) => {
    setNameOverrides((prev) => ({ ...prev, [index]: name }));
  };

  const canContinueFromPreview =
    !!preview && !!basketName.trim() && checkedCount > 0;

  // ── Step 3: Commit ──────────────────────────────────────────────────
  const handleCommit = async () => {
    if (!bundle || !preview) return;

    // Build payload — only include name overrides that actually differ
    // from the bundle's original name (sending a no-op string would
    // still be accepted, just keeps the wire small).
    const overridesPayload: Record<string, string> = {};
    Object.entries(nameOverrides).forEach(([k, v]) => {
      const idx = Number(k);
      const bundled = preview.strategies.find((s) => s.strategy_index === idx);
      if (bundled && v.trim() && v.trim() !== bundled.strategy_name) {
        overridesPayload[k] = v.trim();
      }
    });

    const selectionsPayload: Record<string, boolean> = {};
    Object.entries(selections).forEach(([k, v]) => {
      selectionsPayload[k] = v;
    });

    try {
      setCommitting(true);
      const result = await basketIoService.commitImport({
        bundle,
        basket_name_override:
          basketName.trim() === preview.basket.suggested_name
            ? undefined
            : basketName.trim(),
        selections: selectionsPayload,
        name_overrides: overridesPayload,
      });
      const skipText =
        result.skipped_count > 0 ? `, ${result.skipped_count} skipped` : '';
      showSuccess(
        `Imported "${result.basket_name}" with ${result.strategy_count} ${result.strategy_count === 1 ? 'strategy' : 'strategies'}${skipText}`,
      );
      onImported?.(result.basket_id, result.basket_name, result.strategy_count);
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to import bundle';
      showError(msg);
    } finally {
      setCommitting(false);
    }
  };

  if (!isOpen) return null;

  const inFlight = parsing || committing;

  return (
    <Modal
      isOpen={isOpen}
      onClose={inFlight ? () => {} : onClose}
      maxWidth="2xl"
      ariaLabel="Import basket"
      closeOnBackdrop={!inFlight}
      closeOnEscape={!inFlight}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white inline-flex items-center gap-2">
            <Download className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            Import basket
          </h2>
          <Stepper current={step} />
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={inFlight}
          aria-label="Close"
          className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {step === 'upload' && (
          <UploadStep
            dragging={dragging}
            parsing={parsing}
            onPickFile={() => fileInputRef.current?.click()}
            onDragEnter={() => setDragging(true)}
            onDragLeave={() => setDragging(false)}
            onDrop={(file) => {
              setDragging(false);
              handleFile(file);
            }}
          />
        )}
        {step === 'preview' && preview && (
          <PreviewStep
            preview={preview}
            basketName={basketName}
            onBasketNameChange={setBasketName}
            selections={selections}
            nameOverrides={nameOverrides}
            allSupportedChecked={allSupportedChecked}
            someSupportedChecked={someSupportedChecked}
            onToggleAll={toggleAllSupported}
            onToggleStrategy={toggleStrategy}
            onStrategyNameChange={setStrategyName}
            unsupported={unsupportedStrategies}
          />
        )}
        {step === 'confirm' && preview && (
          <ConfirmStep
            basketName={basketName}
            preview={preview}
            selections={selections}
            nameOverrides={nameOverrides}
          />
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 px-6 py-3 border-t border-gray-200 dark:border-gray-700">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {step === 'upload' && 'Drop a .json bundle to begin'}
          {step === 'preview' &&
            `${checkedCount} of ${supportedStrategies.length} supported strategies selected`}
          {step === 'confirm' &&
            `${checkedCount} ${checkedCount === 1 ? 'strategy' : 'strategies'} will be imported`}
        </span>
        <div className="flex items-center gap-2">
          {step === 'preview' && (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => setStep('upload')}
              disabled={inFlight}
            >
              Back
            </Button>
          )}
          {step === 'confirm' && (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => setStep('preview')}
              disabled={inFlight}
            >
              Back
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose} disabled={inFlight}>
            Cancel
          </Button>
          {step === 'upload' && (
            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              isLoading={parsing}
              disabled={parsing}
            >
              {parsing ? 'Reading…' : 'Choose file'}
            </Button>
          )}
          {step === 'preview' && (
            <Button
              size="sm"
              rightIcon={<ArrowRight className="h-4 w-4" />}
              disabled={!canContinueFromPreview}
              onClick={() => setStep('confirm')}
            >
              Continue
            </Button>
          )}
          {step === 'confirm' && (
            <Button
              size="sm"
              leftIcon={<Download className="h-4 w-4" />}
              isLoading={committing}
              disabled={committing}
              onClick={handleCommit}
            >
              {committing ? 'Importing…' : 'Import'}
            </Button>
          )}
        </div>
      </div>

      {/* Hidden input — used by both the drop zone and the "Choose file" button */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          // Reset so picking the same file twice still fires onChange.
          e.target.value = '';
        }}
      />
    </Modal>
  );
};

// ─── Stepper ──────────────────────────────────────────────────────────
const Stepper: React.FC<{ current: Step }> = ({ current }) => {
  const steps: { id: Step; label: string }[] = [
    { id: 'upload', label: 'Upload' },
    { id: 'preview', label: 'Preview' },
    { id: 'confirm', label: 'Confirm' },
  ];
  const currentIndex = steps.findIndex((s) => s.id === current);
  return (
    <ol className="mt-2 flex items-center gap-2 text-xs">
      {steps.map((s, i) => {
        const done = i < currentIndex;
        const active = i === currentIndex;
        return (
          <li key={s.id} className="flex items-center gap-2">
            {/*
              Step-indicator series — intentionally inline (NOT in chipColors).
              The `done` (bg-emerald-100) and `active` (bg-emerald-600) states
              share an emerald hue so the series reads as a cohesive progress
              bar; canonicalizing `done` to CHIP_DENSE.COMPLETE (green) would
              split the hues across the series. Step indicators are not chips.
            */}
            <span
              className={
                done
                  ? 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                  : active
                    ? 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-white font-semibold'
                    : 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
              }
            >
              {done ? <CheckCircle2 className="h-3 w-3" /> : i + 1}
            </span>
            <span
              className={
                active
                  ? 'font-medium text-gray-900 dark:text-white'
                  : done
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : 'text-gray-500 dark:text-gray-400'
              }
            >
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <span
                className={
                  done
                    ? 'h-px w-8 bg-emerald-300 dark:bg-emerald-700'
                    : 'h-px w-8 bg-gray-200 dark:bg-gray-700'
                }
              />
            )}
          </li>
        );
      })}
    </ol>
  );
};

// ─── Step 1: Upload ───────────────────────────────────────────────────
const UploadStep: React.FC<{
  dragging: boolean;
  parsing: boolean;
  onPickFile: () => void;
  onDragEnter: () => void;
  onDragLeave: () => void;
  onDrop: (file: File | undefined) => void;
}> = ({ dragging, parsing, onPickFile, onDragEnter, onDragLeave, onDrop }) => (
  <div
    className={
      'border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer ' +
      (dragging
        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/10'
        : 'border-gray-300 dark:border-gray-600 hover:border-emerald-400 hover:bg-emerald-50/30 dark:hover:bg-emerald-900/5')
    }
    onClick={parsing ? undefined : onPickFile}
    onDragEnter={(e) => {
      e.preventDefault();
      onDragEnter();
    }}
    onDragOver={(e) => e.preventDefault()}
    onDragLeave={(e) => {
      e.preventDefault();
      onDragLeave();
    }}
    onDrop={(e) => {
      e.preventDefault();
      onDrop(e.dataTransfer.files?.[0]);
    }}
  >
    <FileJson className="w-10 h-10 mx-auto text-gray-400" />
    <p className="mt-3 text-sm font-medium text-gray-900 dark:text-white">
      {parsing ? 'Reading bundle…' : 'Drop bundle file here, or click to browse'}
    </p>
    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
      JSON files up to 1 MB · exported from another account or this one
    </p>
  </div>
);

// ─── Step 2: Preview ──────────────────────────────────────────────────
const PreviewStep: React.FC<{
  preview: ImportPreview;
  basketName: string;
  onBasketNameChange: (v: string) => void;
  selections: Record<number, boolean>;
  nameOverrides: Record<number, string>;
  allSupportedChecked: boolean;
  someSupportedChecked: boolean;
  onToggleAll: () => void;
  onToggleStrategy: (index: number) => void;
  onStrategyNameChange: (index: number, name: string) => void;
  unsupported: ImportPreviewStrategy[];
}> = ({
  preview,
  basketName,
  onBasketNameChange,
  selections,
  nameOverrides,
  allSupportedChecked,
  someSupportedChecked,
  onToggleAll,
  onToggleStrategy,
  onStrategyNameChange,
  unsupported,
}) => (
  <>
    {/* Basket name */}
    <div className="space-y-1.5">
      <label className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
        Basket name
      </label>
      <Input
        value={basketName}
        onChange={(e) => onBasketNameChange(e.target.value)}
        placeholder="Basket name"
      />
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Auto-suggested from <span className="font-medium">"{preview.basket.basket_name}"</span>.
        Imported as <span className="font-medium">INACTIVE</span>; review before activating.
      </p>
    </div>

    {/* Supported strategies */}
    <div className="space-y-2">
      <label className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 cursor-pointer">
        <span className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={allSupportedChecked}
            ref={(el) => {
              if (el) el.indeterminate = someSupportedChecked;
            }}
            onChange={onToggleAll}
            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-2 focus:ring-emerald-500"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
            Strategies in bundle ({preview.summary.total})
          </span>
        </span>
      </label>

      {preview.strategies.map((s) => {
        const checked = !!selections[s.strategy_index];
        const overrideName =
          nameOverrides[s.strategy_index] ?? s.strategy_name;
        return (
          <div
            key={s.strategy_index}
            className={
              'rounded-lg border p-3 transition-colors ' +
              (!s.supported
                ? 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10'
                : checked
                  ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/40 dark:bg-emerald-900/10'
                  : 'border-gray-200 dark:border-gray-700')
            }
          >
            <div className="flex items-start gap-3">
              <Checkbox
                checked={checked}
                disabled={!s.supported}
                onChange={() => onToggleStrategy(s.strategy_index)}
              />
              <div className="flex-1 min-w-0">
                {s.supported ? (
                  <Input
                    value={overrideName}
                    onChange={(e) =>
                      onStrategyNameChange(s.strategy_index, e.target.value)
                    }
                    className="text-sm font-medium"
                  />
                ) : (
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {s.strategy_name}
                  </div>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  {s.underlying && <Badge variant="info" size="sm">{s.underlying}</Badge>}
                  {s.expiry_type && (
                    <Badge variant="default" size="sm">
                      {s.expiry_type.toUpperCase()}
                    </Badge>
                  )}
                  {s.product && <Badge variant="default" size="sm">{s.product}</Badge>}
                  {s.trading_type && (
                    <Badge variant="default" size="sm">{s.trading_type}</Badge>
                  )}
                  <span>
                    {s.leg_count} {s.leg_count === 1 ? 'leg' : 'legs'}
                  </span>
                </div>
                {!s.supported && (
                  <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-400 inline-flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Underlying <span className="font-mono">{s.underlying}</span> not
                    available in this account — will be skipped
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>

    {unsupported.length > 0 && (
      <p className="text-xs text-amber-700 dark:text-amber-400">
        {unsupported.length} {unsupported.length === 1 ? 'strategy is' : 'strategies are'} flagged as unsupported in this tenant and will not be imported.
      </p>
    )}
  </>
);

// ─── Step 3: Confirm ──────────────────────────────────────────────────
const ConfirmStep: React.FC<{
  basketName: string;
  preview: ImportPreview;
  selections: Record<number, boolean>;
  nameOverrides: Record<number, string>;
}> = ({ basketName, preview, selections, nameOverrides }) => {
  const willImport = preview.strategies.filter(
    (s) => s.supported && selections[s.strategy_index],
  );
  const willSkip = preview.strategies.filter(
    (s) => !s.supported || !selections[s.strategy_index],
  );
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
            New basket
          </div>
          <div className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
            {basketName}
          </div>
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Strategies to import ({willImport.length})
          </div>
          <ul className="mt-1.5 space-y-1">
            {willImport.map((s) => {
              const finalName =
                nameOverrides[s.strategy_index]?.trim() || s.strategy_name;
              return (
                <li
                  key={s.strategy_index}
                  className="text-sm text-gray-700 dark:text-gray-200 inline-flex items-center gap-2"
                >
                  <FileText className="h-3.5 w-3.5 text-gray-400" />
                  <span>{finalName}</span>
                  <Badge variant="info" size="sm">{s.underlying}</Badge>
                </li>
              );
            })}
          </ul>
        </div>
        {willSkip.length > 0 && (
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-amber-600 dark:text-amber-400">
              Will be skipped ({willSkip.length})
            </div>
            <ul className="mt-1.5 space-y-1">
              {willSkip.map((s) => (
                <li
                  key={s.strategy_index}
                  className="text-xs text-amber-700 dark:text-amber-400"
                >
                  {s.strategy_name}{' '}
                  <span className="text-gray-500 dark:text-gray-500">
                    ({!s.supported ? `${s.underlying} not supported` : 'unchecked'})
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        The new basket will be created with status <span className="font-medium">INACTIVE</span> and no broker allocations.
        Set up allocations on the Basket page after import.
      </p>
    </div>
  );
};

export default ImportBasketDialog;
